// PropertyLockService.jsx
// Service for property-based smart lock management with authentication

import axios from "axios";
import { getAuth } from "firebase/auth";

// Axios instance with authentication
const apiClient = axios.create({
  // baseURL: "https://ai-realtor-seam.onrender.com/api",
  baseURL: `${process.env.REACT_APP_SEAM_SERVER_URL}/api`,

  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Add Firebase authentication token to requests
 */
apiClient.interceptors.request.use(
  async (config) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Tenant-specific API client (no Firebase auth)
const tenantApiClient = axios.create({
  baseURL: `${process.env.REACT_APP_SEAM_SERVER_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Add tenant ID to requests
 */
tenantApiClient.interceptors.request.use(
  (config) => {
    // Get tenant ID from localStorage
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      config.headers['x-tenant-id'] = tenantId;
      // Also add as query parameter for fallback
      if (config.params) {
        config.params.tenantId = tenantId;
      } else {
        config.params = { tenantId };
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Associate a lock with a specific property
 * @param {string} listingId - The property/listing ID
 * @param {string} lockId - The smart lock device ID
 * @returns {Promise<object>} - Association result
 * @throws {Error} - If the request fails
 */
export const associateLockWithProperty = async (listingId, lockId) => {
  if (!listingId || !lockId) {
    throw new Error("Both listingId and lockId are required to associate a lock with a property.");
  }

  try {
    const response = await apiClient.post(`/properties/${listingId}/locks`, {
      lockId,
    });
    return response.data;
  } catch (error) {
    console.error(
      `Error associating lock ${lockId} with property ${listingId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get all locks for a specific property
 * @param {string} listingId - The property/listing ID
 * @returns {Promise<Array>} - Array of locks or empty array if none found
 * @throws {Error} - If the request fails
 */
export const getLocksByProperty = async (listingId) => {
  if (!listingId) {
    throw new Error("Listing ID is required to fetch property locks.");
  }

  try {
    const response = await apiClient.get(`/properties/${listingId}/locks`);
    return response.data.locks || [];
  } catch (error) {
    if (error.response?.status === 404) {
      // No locks associated with this property
      return [];
    }
    console.error(
      `Error fetching locks for property ${listingId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Remove a specific lock from a property
 * @param {string} listingId - The property/listing ID
 * @param {string} lockId - The lock device ID to remove
 * @returns {Promise<object>} - Disassociation result
 * @throws {Error} - If the request fails
 */
export const removeLockFromProperty = async (listingId, lockId) => {
  if (!listingId || !lockId) {
    throw new Error("Both listingId and lockId are required to remove lock.");
  }

  try {
    const response = await apiClient.delete(`/properties/${listingId}/locks/${lockId}`);
    return response.data;
  } catch (error) {
    console.error(
      `Error removing lock ${lockId} from property ${listingId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get all property-lock associations for the authenticated user
 * @returns {Promise<Array>} - Array of property-lock associations
 * @throws {Error} - If the request fails
 */
export const getAllPropertyLocks = async () => {
  try {
    const response = await apiClient.get("/properties/locks/all");
    return response.data.associations;
  } catch (error) {
    console.error(
      "Error fetching all property-lock associations:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Update lock with property information
 * @param {string} listingId - The property/listing ID
 * @param {string} lockId - The smart lock device ID
 * @param {object} propertyInfo - Property details (address, city, state)
 * @returns {Promise<object>} - Update result
 * @throws {Error} - If the request fails
 */
export const updateLockWithPropertyInfo = async (listingId, lockId, propertyInfo) => {
  if (!listingId || !lockId || !propertyInfo) {
    throw new Error("listingId, lockId, and propertyInfo are required to update lock.");
  }

  try {
    const response = await apiClient.put(`/properties/${listingId}/locks/${lockId}`, {
      propertyInfo,
    });
    return response.data;
  } catch (error) {
    console.error(
      `Error updating lock ${lockId} with property info:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get user's available locks for a specific property
 * Returns all user's locks that are NOT associated with ANY property
 * @param {string} currentListingId - The current property ID (optional, for context)
 * @returns {Promise<Array>} - List of available smart locks (not associated with any property)
 * @throws {Error} - If the request fails
 */
export const getAvailableLocksForProperty = async (currentListingId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User must be authenticated to fetch locks.");
    }

    // Fetch all user locks
    const response = await apiClient.get("/user-locks", {
      params: { uid: user.uid },
    });

    const allLocks = response.data.devices || [];

    // Get ALL property-lock associations for this user
    let allAssociatedLockIds = new Set();
    try {
      const allAssociations = await getAllPropertyLocks();
      // Extract all lock IDs that are associated with ANY property
      allAssociations.forEach(association => {
        if (association.locks && Array.isArray(association.locks)) {
          association.locks.forEach(lock => {
            allAssociatedLockIds.add(lock.lockId);
          });
        }
      });
    } catch (err) {
      // If error fetching associations, continue with empty set
      console.log("Could not fetch property associations:", err.message);
    }

    // Filter out locks that are already associated with ANY property
    const availableLocks = allLocks.filter(lock => !allAssociatedLockIds.has(lock.lockId));

    console.log(`[getAvailableLocksForProperty] Total locks: ${allLocks.length}, Associated: ${allAssociatedLockIds.size}, Available: ${availableLocks.length}`);

    return availableLocks;
  } catch (error) {
    console.error(
      "Error fetching available locks:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get all locks for a specific property (Tenant Access)
 * @param {string} listingId - The property/listing ID
 * @returns {Promise<Array>} - Array of locks or empty array if none found
 * @throws {Error} - If the request fails
 */
export const getLocksByPropertyForTenant = async (listingId) => {
  if (!listingId) {
    throw new Error("Listing ID is required to fetch property locks.");
  }

  try {
    const response = await tenantApiClient.get(`/properties/${listingId}/locks/tenant`);
    return response.data.locks || [];
  } catch (error) {
    if (error.response?.status === 404) {
      // No locks associated with this property
      return [];
    }
    console.error(
      `Error fetching locks for property ${listingId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

